import { classifyGroupAction, type EligibilityVerdict } from '@tool-no-hu/shared/utils';
import type { BulkGroupAction, IGroupData, IAccountDataResponse } from '@tool-no-hu/shared/types';

/**
 * Điều kiện `start`/`stop` của một nhóm, tính ở PHÍA WEB để preview (bật/tắt nút,
 * đếm số nhóm đủ điều kiện, hiện lý do bỏ qua trong bulk).
 *
 * Luật dùng CHUNG với server (`classifyGroupAction` trong gói shared) — ở đây chỉ
 * dựng `ctx` từ dữ liệu web: trạng thái chạy suy từ `member.status`, đăng nhập
 * suy từ `member.accessToken`. Server vẫn là nguồn sự thật (đọc WS thật); lệch
 * thì server skip đúng.
 */
export type GroupEligibility = Record<BulkGroupAction, EligibilityVerdict>;

/**
 * Resolve members của một nhóm thành `IAccountDataResponse` đã merge với
 * `accounts` state — GIỐNG cách `GroupCard` làm: account state là nguồn thật cho
 * status/token/balance, còn config thì ưu tiên `group.members`.
 */
export function resolveGroupMembers(
  group: IGroupData,
  accounts: IAccountDataResponse[],
): IAccountDataResponse[] {
  const members = ((group.members || []) as IAccountDataResponse[]).filter(
    (m): m is IAccountDataResponse => typeof m !== 'string',
  );
  return members.map((m) => {
    const fromState = accounts.find((a) => a._id === m._id || a.username === m.username);
    return fromState ? { ...fromState, ...m } : m;
  });
}

/**
 * Nhóm đang chạy = MỌI thành viên `RUNNING`.
 *
 * Phải khớp ĐÚNG server (`isLivestreamRunning` cho mọi member) và cách lobby chia
 * section running/idle — cả 3 nơi dùng `every`. Nếu ở đây dùng `some`, nhóm chạy
 * dở (vd 2/4) sẽ bị preview coi là "đang chạy" (chặn Start oan, hứa Stop) trong
 * khi server coi là chưa chạy (Start được, Stop bị skip) → badge/hint lệch hành vi
 * server. Nhóm sun cần đúng 4 account cùng đánh nên "đang chạy" = đủ 4 đang chạy.
 */
export function isGroupRunning(members: IAccountDataResponse[]): boolean {
  return members.length > 0 && members.every((m) => m.status === 'RUNNING');
}

export function groupEligibility(
  group: IGroupData,
  accounts: IAccountDataResponse[],
): GroupEligibility {
  const members = resolveGroupMembers(group, accounts);
  const ctx = {
    running: isGroupRunning(members),
    allLoggedIn: members.length > 0 && members.every((m) => !!m.accessToken),
    memberCount: members.length,
  };

  return {
    start: classifyGroupAction('start', ctx),
    stop: classifyGroupAction('stop', ctx),
  };
}
